import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import Auth from "./components/Auth";
import AuthCallback from "./components/AuthCallback";
import AuthUpdate from "./components/AuthUpdate";
import CaseManagement from "./components/CaseManagement";
import ChatInterface from "./components/ChatInterface";
import EmailConfirmation from "./components/EmailConfirmation";
import FileManagement from "./components/FileManagement";
import FirmSettings from "./components/FirmSettings";
import LandingPage from "./components/LandingPage";
import Layout from "./components/Layout";
import MedicalChronology from "./components/MedicalChronology";
import PhysicianManagement from "./components/PhysicianManagement";
import ReviewPreferences from "./components/ReviewPreferences";
import UserManagement from "./components/UserManagement";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

const handleUnhandledRejection = (event) => {
  console.error("[Global] Unhandled Promise Rejection:", {
    reason: event.reason,
    stack: event.reason?.stack,
    location: window.location.href,
    reasonType: Object.prototype.toString.call(event.reason),
    path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
};

window.addEventListener("unhandledrejection", handleUnhandledRejection);

const InitialRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleInitialRedirect = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      navigate("/cases");
      setIsLoading(false);
    };

    handleInitialRedirect();
  }, [user, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return null;
};

const AppContent = () => {
  const { user, loading } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
  }, [user, loading]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading application. Please refresh the page.</div>;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/cases" /> : <LandingPage />}
      />
      <Route
        path="/auth"
        element={user ? <Navigate to="/cases" /> : <Auth />}
      />
      <Route
        path="/email-confirmation"
        element={
          user && user.email_confirmed_at ? (
            <Navigate to="/" />
          ) : (
            <EmailConfirmation />
          )
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/update" element={<AuthUpdate />} />

      {user && user.email_confirmed_at ? (
        <Route
          element={
            <Layout>
              <Outlet />
            </Layout>
          }
        >
          <Route path="/cases" element={<CaseManagement />} />
          <Route path="/chat" element={<ChatInterface />} />
          <Route
            path="/cases/:caseId/files"
            element={<FileManagementWrapper />}
          />
          <Route
            path="/cases/:caseId/chronology"
            element={<MedicalChronology />}
          />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/firm-settings" element={<FirmSettings />} />
          <Route path="/preferences" element={<ReviewPreferences />} />
          <Route
            path="/preferences/document-types/:documentType"
            element={<ReviewPreferences />}
          />
          <Route
            path="/preferences/document-types/:documentType/visit-types/:visitType"
            element={<ReviewPreferences />}
          />
          <Route path="/physicians" element={<PhysicianManagement />} />
          <Route path="/templates" element={<ReviewPreferences />} />
          <Route
            path="/templates/:templateId"
            element={<ReviewPreferences />}
          />
          <Route
            path="/templates/:templateId/document-types/:documentType"
            element={<ReviewPreferences />}
          />
          <Route
            path="/templates/:templateId/document-types/:documentType/visit-types/:visitType"
            element={<ReviewPreferences />}
          />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/" />} />
      )}
    </Routes>
  );
};

const FileManagementWrapper = () => {
  const context = useOutletContext();
  return <FileManagement {...context} />;
};

const App = () => {
  useEffect(() => {
    const handleError = (event) => {
      console.error("[App] Error caught:", {
        error: event.error,
        message: event.message,
        stack: event.error?.stack,
        location: window.location.href,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  return (
    <>
      <Toaster position="top-right" />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </>
  );
};

export default App;
